/**
 * GA4 conversion tracking for the dashboard (dash.staty.io).
 *
 * Same GA4 property as the landing site (G-S6QSZRF1RP) with cross-domain
 * linking, so ad click → landing CTA → sign-up → activation reads as ONE
 * funnel in GA instead of two unrelated sessions.
 *
 * Loads ONLY on the production host: localhost/previews never send data, and
 * every helper no-ops when gtag is absent (tests, adblock, non-prod), so call
 * sites never need to guard.
 *
 * Conversion events (mark as key events in the GA4 admin UI):
 *   sign_up_started · sign_up · org_created · registration_published ·
 *   payments_connected
 */
export const GA4_ID = 'G-S6QSZRF1RP';
const PROD_HOST = 'dash.staty.io';

type GtagWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };

export function initAnalytics(): void {
  if (typeof window === 'undefined') return;
  const w = window as GtagWindow;
  if (window.location.hostname !== PROD_HOST || w.gtag) return;

  w.dataLayer = w.dataLayer || [];
  // Official gtag snippet: GA4 requires pushing the `arguments` OBJECT (an
  // array would be read as a classic GTM message and the command ignored).
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    (w.dataLayer as unknown[]).push(arguments);
  }
  w.gtag = gtag;

  gtag('js', new Date());
  // Cross-domain: keep the client id across the staty.io → dash.staty.io hop
  // (the landing side sets the matching linker).
  gtag('set', 'linker', { domains: ['staty.io', 'dash.staty.io'], accept_incoming: true });
  // SPA: page_views are sent from the router subscription, not on config.
  gtag('config', GA4_ID, { send_page_view: false });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);
}

export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const gtag = (window as GtagWindow).gtag;
  if (typeof gtag !== 'function') return;
  gtag('event', name, params || {});
}

export function trackPageView(path: string): void {
  trackEvent('page_view', { page_path: path });
}

/**
 * SPA page_view tracking off the react-router data router: fires the initial
 * view and one per navigation (deduped by path — router state can emit
 * several times per transition).
 */
export function initPageViewTracking(router: {
  state: { location: { pathname: string } };
  subscribe: (fn: (state: { location: { pathname: string } }) => void) => void;
}): void {
  if (typeof window === 'undefined') return;
  if (window.location.hostname !== PROD_HOST) return;
  let lastPath = router.state.location.pathname;
  trackPageView(lastPath);
  router.subscribe((state) => {
    const path = state.location.pathname;
    if (path === lastPath) return;
    lastPath = path;
    trackPageView(path);
  });
}
